import React from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { SectionNav } from '../components/SectionNav';
type SkillGroup = {
  title: string;
  note: string;
  skills: {
    name: string;
    level: number;
  }[];
};
const GROUPS: SkillGroup[] = [{
  title: 'Regulatory Reporting & Compliance',
  note: 'Preparation, review, and defence of US bank regulatory filings.',
  skills: [{
    name: 'Federal Reserve & OCC reporting',
    level: 98
  }, {
    name: 'Regulation W (23A / 23B)',
    level: 96
  }, {
    name: 'Supervisory remediation (MRA / MIRA)',
    level: 95
  }, {
    name: 'FFIEC & FR reporting suites',
    level: 94
  }]
}, {
  title: 'Financial Control & Technical Accounting',
  note: 'Control environment ownership and technical accounting judgement.',
  skills: [{
    name: 'US GAAP',
    level: 97
  }, {
    name: 'IFRS',
    level: 90
  }, {
    name: 'Internal controls & SOX',
    level: 93
  }, {
    name: 'Month-end close & consolidation',
    level: 92
  }]
}, {
  title: 'Capital, Liquidity & Treasury',
  note: 'Balance sheet stewardship in partnership with global treasury.',
  skills: [{
    name: 'Capital adequacy frameworks',
    level: 95
  }, {
    name: 'Liquidity oversight',
    level: 90
  }, {
    name: 'Balance sheet & funding analysis',
    level: 91
  }, {
    name: 'Capital optimization strategy',
    level: 94
  }]
}, {
  title: 'Leadership & Governance',
  note: 'Executive communication, board reporting, and team stewardship.',
  skills: [{
    name: 'Board & committee reporting',
    level: 95
  }, {
    name: 'Regulator & auditor engagement',
    level: 96
  }, {
    name: 'Cross-border team leadership',
    level: 92
  }, {
    name: 'Budgeting & strategic planning',
    level: 90
  }]
}];
const SOFT_SKILLS = ['Executive presence', 'Regulatory diplomacy', 'Analytical rigour', 'Change leadership', 'Stakeholder influence', 'Commercial pragmatism', 'Mentoring & succession', 'Crisis composure'];
export function Skills() {
  return <>
      <PageHeader index="03" title="Skills" intro="A functional capability inventory: the finance disciplines Gerry personally owns, independent of any single employer or platform." data-id="element-388" data-mp="1b9ij2v02sadh" />

      <section aria-labelledby="skill-groups" className="w-full bg-white" data-id="element-389" data-mp="weud7fqrj9xc">
        <div className="mx-auto max-w-content px-6 py-24 sm:py-28 lg:px-8" data-id="element-390" data-mp="wgysetu43vcx">
          <h2 id="skill-groups" className="text-[11px] font-semibold uppercase tracking-widest text-navy-700" data-id="element-391" data-mp="1u1tbtw10mrxv">
            Core Functional Disciplines
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12" data-id="element-392" data-mp="1kaqen5b7ix3v">
            {GROUPS.map((group, gi) => <motion.article key={group.title} initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: gi % 2 * 0.08
          }} className="border-t-2 border-navy-900 bg-slateink-100 p-8" data-id="element-393" data-mp="1fbm2q6wwel9u" data-mpi={gi}>
                <h3 className="font-serif text-xl text-navy-900" data-id="element-394" data-mp="2keg1qpchmuj0">{group.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slateink-600" data-id="element-395" data-mp="2p9vflhahwqff">
                  {group.note}
                </p>

                <ul className="mt-8 space-y-6" data-id="element-396" data-mp="2z9m85dfhwvcv">
                  {group.skills.map(skill => <li key={skill.name} data-id="element-397" data-mp="33b7t7m26ofy5">
                      <div className="flex items-baseline justify-between gap-4" data-id="element-398" data-mp="33a66gkbt250l">
                        <span className="text-[15px] text-navy-900" data-id="element-399" data-mp="381o5h2u9pvix">{skill.name}</span>
                        <span className="font-serif text-xs text-slateink-500" data-id="element-400" data-mp="3d0r8yiulntos">
                          {skill.level}
                        </span>
                      </div>
                      <div className="mt-2.5 h-[3px] w-full bg-slateink-300" role="presentation" data-id="element-401" data-mp="3hyapjpersijw">
                        <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: `${skill.level}%`
                  }} viewport={{
                    once: true,
                    margin: '-40px'
                  }} transition={{
                    duration: 0.9,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="h-full bg-gold-500" data-id="element-402" data-mp="njmqh22qjpop" />
                      </div>
                    </li>)}
                </ul>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section aria-labelledby="soft-skills" className="w-full bg-navy-950" data-id="element-403" data-mp="dn9zibykrpxn">
        <div className="mx-auto max-w-content px-6 py-20 sm:py-24 lg:px-8" data-id="element-404" data-mp="9log07rjcdeh">
          <h2 id="soft-skills" className="text-[11px] font-semibold uppercase tracking-widest text-gold-400" data-id="element-405" data-mp="9nst9gbbqorf">
            Leadership Attributes
          </h2>
          <ul className="mt-8 flex flex-wrap gap-3" data-id="element-406" data-mp="1l2ob1b59p6c7">
            {SOFT_SKILLS.map(skill => <li key={skill} className="border border-white/15 px-5 py-2.5 text-[12px] uppercase tracking-wider text-slateink-300" data-id="element-407" data-mp="1sm75qenvio0m">
                {skill}
              </li>)}
          </ul>
          <p className="mt-10 max-w-2xl text-sm leading-relaxed text-slateink-500" data-id="element-408" data-mp="1nm2qerypbzbz">
            Platforms, automation tooling, and regulatory report forms are catalogued
            separately in Section 08 — Technical Mastery &amp; Systems Portfolio.
          </p>
        </div>
      </section>

      <SectionNav currentIndex="03" data-id="element-409" data-mp="21lkjhjj15yr5" />
    </>;
}